import { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Array<'admin' | 'coordinateur regional' | 'coordinateur prefectoral' | 'coordinateur sous-prefectoral' | 'relais paysans pour la victoire (VDP)'>;
  requirePhoneVerification?: boolean;
}

export function ProtectedRoute({
  children,
  allowedRoles = [],
}: ProtectedRouteProps) {
  const { authState, checkAuth } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  // Memoize la fonction pour éviter de changer sa référence à chaque render
  const verifyAuth = useCallback(async () => {
    await checkAuth();
    setIsChecking(false);
  }, [checkAuth]);

  useEffect(() => {
    if (isChecking) {
      verifyAuth();
    }
  }, [isChecking, verifyAuth]);

  // Affichage de chargement pendant la vérification
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-2xl text-primary-600 animate-spin"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Vérification de l'authentification...
          </h2>
          <p className="text-gray-600">
            Veuillez patienter pendant que nous vérifions vos permissions.
          </p>
        </div>
      </div>
    );
  }

  // Redirection vers login si non authentifié
  if (!authState.isAuthenticated || !authState.user || authState.user.status != "actif") {
    return <Navigate to="/connexion" state={{ from: location }} replace />;
  }

  // Vérification des rôles autorisés
  if (allowedRoles.length > 0 && authState.role && !allowedRoles.includes(authState.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-600"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Accès non autorisé
          </h2>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Retour
            </button>
            <button
              type="button"
              onClick={() => (window.location.href = '/')}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors cursor-pointer"
            >
              Accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}